import React, { useState, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ChevronLeft, ChevronRight, X, Download, FileText, Image as ImageIcon, Shield } from 'lucide-react';

const ImageGallery = ({ 
  propertyImages = [], 
  verificationDocs = [], 
  subleaseConsentDoc = null,
  propertyTitle = 'Property',
  onClose 
}) => {
  const { colors } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentCategory, setCurrentCategory] = useState('property');

  // Combine all images with metadata
  const allImages = [
    ...propertyImages.map((url, index) => ({
      url,
      type: 'property',
      title: `Property Image ${index + 1}`,
      category: 'Property Photos',
      icon: ImageIcon
    })),
    ...verificationDocs.map((url, index) => ({
      url,
      type: 'verification',
      title: `Verification Document ${index + 1}`,
      category: 'Ownership Proof',
      icon: FileText
    })),
    ...(subleaseConsentDoc ? [{
      url: subleaseConsentDoc,
      type: 'sublease',
      title: 'Sublease Consent Document',
      category: 'Legal Documents',
      icon: Shield
    }] : [])
  ];

  // Filter images by category
  const getImagesByCategory = (category) => {
    return allImages.filter(img => img.type === category);
  };

  const currentImages = currentCategory === 'all' ? allImages : getImagesByCategory(currentCategory);
  const currentImage = currentImages[currentIndex];

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? currentImages.length - 1 : prev - 1));
  }, [currentImages.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === currentImages.length - 1 ? 0 : prev + 1));
  }, [currentImages.length]);

  const handleCategoryChange = (category) => {
    setCurrentCategory(category);
    setCurrentIndex(0);
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [onClose, goToPrevious, goToNext]);

  const downloadImage = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const getCategoryStats = () => {
    return {
      property: propertyImages.length,
      verification: verificationDocs.length,
      sublease: subleaseConsentDoc ? 1 : 0,
      total: allImages.length
    };
  };

  const stats = getCategoryStats();

  if (allImages.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div 
          className="rounded-xl p-8 max-w-md w-full mx-4 text-center"
          style={{ backgroundColor: colors.surface }}
        >
          <ImageIcon className="w-16 h-16 mx-auto mb-4" style={{ color: colors.textMuted }} />
          <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>
            No Images Available
          </h3>
          <p className="mb-6" style={{ color: colors.textSecondary }}>
            This property doesn't have any images or documents to display.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg font-medium text-white"
            style={{ backgroundColor: colors.primary }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-md flex flex-col z-[100]">
      {/* Header absolute or fixed so it's always accessible */}
      <div 
        className="flex items-center justify-between p-4 sm:p-6 w-full"
        style={{ backgroundColor: 'transparent' }}
      >
        <div>
          <h2 className="text-xl sm:text-2xl font-bold drop-shadow-md text-white">
            {propertyTitle}
          </h2>
          <p className="text-sm font-medium opacity-80 text-white drop-shadow-md">
            {currentIndex + 1} of {currentImages.length} • {currentImage?.category}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {currentImage && (
            <button
              onClick={() => downloadImage(currentImage.url, `${propertyTitle}-${currentImage.title}`)}
              className="p-3 rounded-full transition-all hover:scale-105 bg-white/10 text-white hover:bg-white/20 backdrop-blur-md shadow-lg"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>
          )}
          
          <button
            onClick={onClose}
            className="p-3 rounded-full transition-all bg-red-500 text-white hover:bg-red-600 shadow-[0_0_15px_rgba(239,68,68,0.5)] hover:scale-105"
            title="Close Gallery"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Category Filters */}
      <div 
        className="flex items-center gap-3 px-4 sm:px-6 pb-4 overflow-x-auto"
      >
        <button
          onClick={() => handleCategoryChange('all')}
          className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
            currentCategory === 'all' ? 'ring-2' : ''
          }`}
          style={{ 
            backgroundColor: currentCategory === 'all' ? `${colors.primary}30` : colors.background,
            color: currentCategory === 'all' ? colors.primary : colors.text,
            border: `1px solid ${colors.border}`,
            ringColor: colors.primary
          }}
        >
          All ({stats.total})
        </button>
        
        {stats.property > 0 && (
          <button
            onClick={() => handleCategoryChange('property')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              currentCategory === 'property' ? 'ring-2' : ''
            }`}
            style={{ 
              backgroundColor: currentCategory === 'property' ? `${colors.success}30` : colors.background,
              color: currentCategory === 'property' ? colors.success : colors.text,
              border: `1px solid ${colors.border}`,
              ringColor: colors.success
            }}
          >
            <ImageIcon className="w-4 h-4" />
            Property Photos ({stats.property})
          </button>
        )}
        
        {stats.verification > 0 && (
          <button
            onClick={() => handleCategoryChange('verification')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              currentCategory === 'verification' ? 'ring-2' : ''
            }`}
            style={{ 
              backgroundColor: currentCategory === 'verification' ? `${colors.warning}30` : colors.background,
              color: currentCategory === 'verification' ? colors.warning : colors.text,
              border: `1px solid ${colors.border}`,
              ringColor: colors.warning
            }}
          >
            <FileText className="w-4 h-4" />
            Ownership Proof ({stats.verification})
          </button>
        )}
        
        {stats.sublease > 0 && (
          <button
            onClick={() => handleCategoryChange('sublease')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              currentCategory === 'sublease' ? 'ring-2' : ''
            }`}
            style={{ 
              backgroundColor: currentCategory === 'sublease' ? `${colors.secondary}30` : colors.background,
              color: currentCategory === 'sublease' ? colors.secondary : colors.text,
              border: `1px solid ${colors.border}`,
              ringColor: colors.secondary
            }}
          >
            <Shield className="w-4 h-4" />
            Legal Documents ({stats.sublease})
          </button>
        )}
      </div>

      {/* Main Image Display */}
      <div className="flex-1 flex items-center justify-center relative p-4">
        {currentImages.length > 1 && (
          <button
            onClick={goToPrevious}
            className="absolute left-4 z-10 p-3 rounded-full transition-colors"
            style={{ backgroundColor: `${colors.surface}90`, color: colors.text }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        <div className="max-w-4xl max-h-full flex items-center justify-center">
          {currentImage && (
            <div className="relative">
              <img
                src={currentImage.url}
                alt={currentImage.title}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
                style={{ backgroundColor: colors.surface }}
              />
              
              {/* Image Info Overlay */}
              <div 
                className="absolute bottom-4 left-4 right-4 p-3 rounded-lg"
                style={{ backgroundColor: `${colors.surface}95` }}
              >
                <div className="flex items-center gap-2">
                  <currentImage.icon className="w-5 h-5" style={{ color: colors.primary }} />
                  <div>
                    <p className="font-medium" style={{ color: colors.text }}>
                      {currentImage.title}
                    </p>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>
                      {currentImage.category}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {currentImages.length > 1 && (
          <button
            onClick={goToNext}
            className="absolute right-4 z-10 p-3 rounded-full transition-colors"
            style={{ backgroundColor: `${colors.surface}90`, color: colors.text }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Thumbnail Strip */}
      {currentImages.length > 1 && (
        <div 
          className="p-4 border-t"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          <div className="flex gap-2 overflow-x-auto pb-2">
            {currentImages.map((image, index) => {
              const Icon = image.icon;
              return (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all ${
                    index === currentIndex ? 'ring-2' : ''
                  }`}
                  style={{ 
                    backgroundColor: colors.background,
                    ringColor: colors.primary
                  }}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                  <div 
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ 
                      backgroundColor: index === currentIndex ? 'transparent' : `${colors.surface}60`
                    }}
                  >
                    {index !== currentIndex && (
                      <Icon className="w-6 h-6" style={{ color: colors.text }} />
                    )}
                  </div>
                  
                  {/* Type indicator */}
                  <div 
                    className="absolute top-1 right-1 w-3 h-3 rounded-full"
                    style={{ 
                      backgroundColor: image.type === 'property' ? colors.success
                        : image.type === 'verification' ? colors.warning
                        : colors.secondary
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Keyboard Navigation Hint */}
      <div 
        className="text-center py-2 text-xs"
        style={{ color: colors.textMuted, backgroundColor: colors.surface }}
      >
        Use ← → arrow keys to navigate • ESC to close
      </div>
    </div>
  );
};

export default ImageGallery;